import React, { useEffect } from 'react';
import { css } from '@emotion/react';
import { colors, fontFamilies, fontStyles, spacing } from '../../styles';
import { useAutoRotate } from '../PaymentModalByPreference/FeatureShowcase/useAutoRotate';
import { TIMING } from '../PaymentModalByPreference/FeatureShowcase/constants';
import { AIChatAnimation } from '../PaymentModalByPreference/FeatureShowcase/animations/AIChatAnimation';
import { DailyPicksAnimation } from '../PaymentModalByPreference/FeatureShowcase/animations/DailyPicksAnimation';
import { NewsOnChartsAnimation } from '../PaymentModalByPreference/FeatureShowcase/animations/NewsOnChartsAnimation';
import { SmartAlertsAnimation } from '../PaymentModalByPreference/FeatureShowcase/animations/SmartAlertsAnimation';
import { PortfolioAnimation } from '../PaymentModalByPreference/FeatureShowcase/animations/PortfolioAnimation';
import { MarketUpdatesAnimation } from '../PaymentModalByPreference/FeatureShowcase/animations/MarketUpdatesAnimation';

const ACCENT_COLOR = '#D4890B';

const SLIDE_TITLES = [
  { top: 'Your investing', bottom: 'AI expert' },
  { top: 'Research the', bottom: 'entire stock market' },
  { top: 'Read less.', bottom: 'Know more.' },
  { top: 'Never miss', bottom: 'a trade', highlightTop: true },
  { top: 'Catch red flags.', bottom: 'Avoid losses.' },
  { top: 'No finance degree', bottom: 'required' },
];

// Must match Welcome.tsx ANIMATION_TITLES
const ANIMATION_TITLES = [
  'Chat',
  'Copy trading',
  'News',
  'Notifications',
  'Portfolio monitoring',
  'Daily market update',
] as const;

const ANIMATIONS = [
  AIChatAnimation,
  DailyPicksAnimation,
  NewsOnChartsAnimation,
  SmartAlertsAnimation,
  PortfolioAnimation,
  MarketUpdatesAnimation,
] as const;

const TOTAL_SLIDES = SLIDE_TITLES.length;

const VideoShowcasePage: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const intervalMs = params.get('interval') ? Number(params.get('interval')) : Math.floor(30000 / TOTAL_SLIDES);
  const theme = params.get('theme');

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.setAttribute('data-theme', 'light');
    }
  }, [theme]);

  const { currentIndex, progress } = useAutoRotate({
    totalItems: TOTAL_SLIDES,
    intervalMs,
  });

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        background: ${colors.sugar};
        padding: ${spacing.regular};

        html[data-theme='dark'] & {
          background: ${colors.liquorice};
        }
      `}
    >
      {/* Rotating title */}
      <div
        css={css`
          position: relative;
          height: 110px;
          width: 100%;
          max-width: 400px;
          margin-bottom: ${spacing.regular};
        `}
      >
        {SLIDE_TITLES.map((title, index) => (
          <div
            key={index}
            css={css`
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              text-align: center;
              opacity: ${index === currentIndex ? 1 : 0};
              transform: ${index === currentIndex ? 'translateY(0)' : 'translateY(12px)'};
              transition:
                opacity ${TIMING.transitionDuration}ms ease-out,
                transform ${TIMING.transitionDuration}ms ease-out;
            `}
          >
            {[title.top, title.bottom].map((text, lineIndex) => {
              const isHighlighted = title.highlightTop ? lineIndex === 0 : lineIndex === 1;
              return (
                <div
                  key={lineIndex}
                  css={css`
                    font-family: ${fontFamilies.helveticaNowDisplayBold};
                    font-size: 2.25rem;
                    line-height: 1.15;
                    color: ${isHighlighted ? ACCENT_COLOR : colors.liquorice};
                    ${isHighlighted ? 'display: inline-flex; flex-direction: column; align-items: center;' : ''}
                    ${lineIndex === 1 ? 'margin-top: 2px;' : ''}

                    html[data-theme='dark'] & {
                      color: ${isHighlighted ? ACCENT_COLOR : colors.sugar};
                    }
                  `}
                >
                  {text}
                  {isHighlighted && (
                    <svg
                      width="100%"
                      height="8"
                      viewBox="0 0 200 8"
                      preserveAspectRatio="none"
                      css={css`
                        margin-top: 2px;
                        overflow: visible;
                      `}
                    >
                      <path
                        d="M4 5 Q50 2, 100 4.5 T196 3.5"
                        fill="none"
                        stroke={ACCENT_COLOR}
                        strokeWidth="3.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Animation box */}
      <div
        css={css`
          width: 100%;
          max-width: 400px;
        `}
      >
        <div
          css={css`
            position: relative;
            height: 380px;
            overflow: hidden;
            border-radius: 20px;
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            box-shadow:
              0 1px 3px rgba(0, 0, 0, 0.04),
              0 4px 12px rgba(0, 0, 0, 0.06);

            html[data-theme='dark'] & {
              background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
              box-shadow:
                0 1px 3px rgba(0, 0, 0, 0.2),
                0 4px 12px rgba(0, 0, 0, 0.3);
            }
          `}
        >
          {/* Title overlay at top — matches Welcome.tsx */}
          <div
            css={css`
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              z-index: 10;
              padding: ${spacing.deci} ${spacing.deci} ${spacing.centi};
              background: linear-gradient(
                180deg,
                rgba(248, 249, 250, 0.95) 0%,
                rgba(248, 249, 250, 0.8) 60%,
                rgba(248, 249, 250, 0) 100%
              );

              html[data-theme='dark'] & {
                background: linear-gradient(
                  180deg,
                  rgba(26, 26, 26, 0.95) 0%,
                  rgba(26, 26, 26, 0.8) 60%,
                  rgba(26, 26, 26, 0) 100%
                );
              }
            `}
          >
            <div
              css={css`
                ${fontStyles.lion};
                color: ${colors.liquorice};

                html[data-theme='dark'] & {
                  color: ${colors.sugar};
                }
              `}
            >
              {ANIMATION_TITLES[currentIndex]}
            </div>
          </div>

          {/* Animation area */}
          <div
            css={css`
              position: absolute;
              top: 48px;
              left: 0;
              right: 0;
              bottom: 0;
            `}
          >
            {ANIMATIONS.map((Component, index) => (
              <div
                key={index}
                css={css`
                  position: absolute;
                  top: 0;
                  left: 0;
                  right: 0;
                  bottom: 0;
                  opacity: ${index === currentIndex ? 1 : 0};
                  transform: ${index === currentIndex ? 'translateX(0)' : 'translateX(8px)'};
                  transition:
                    opacity ${TIMING.transitionDuration}ms ease-out,
                    transform ${TIMING.transitionDuration}ms ease-out;
                  pointer-events: ${index === currentIndex ? 'auto' : 'none'};
                `}
              >
                <Component isActive={index === currentIndex} />
              </div>
            ))}
          </div>
        </div>

        {/* Progress dots */}
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            gap: ${spacing.centi};
            margin-top: ${spacing.deci};
          `}
        >
          {SLIDE_TITLES.map((_, index) => {
            const isActive = index === currentIndex;
            return (
              <div
                key={index}
                css={css`
                  position: relative;
                  width: ${isActive ? '24px' : '8px'};
                  height: 8px;
                  background: ${isActive ? 'transparent' : colors.cloud};
                  border-radius: 4px;
                  transition: all 0.3s ease;
                  overflow: hidden;

                  html[data-theme='dark'] & {
                    background: ${isActive ? 'transparent' : colors.tungsten};
                  }
                `}
              >
                {isActive && (
                  <>
                    <div
                      css={css`
                        position: absolute;
                        top: 0;
                        left: 0;
                        width: 100%;
                        height: 100%;
                        background: ${colors.cloud};
                        border-radius: 4px;

                        html[data-theme='dark'] & {
                          background: ${colors.tungsten};
                        }
                      `}
                    />
                    <div
                      css={css`
                        position: absolute;
                        top: 0;
                        left: 0;
                        height: 100%;
                        width: ${progress}%;
                        background: ${colors.petrolBlue};
                        border-radius: 4px;
                        transition: width 50ms linear;

                        html[data-theme='dark'] & {
                          background: ${colors.skyBlue};
                        }
                      `}
                    />
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default VideoShowcasePage;
